import { create } from "zustand";
import type { TeacherListRow } from "@/features/teachers/types/teacher.types";
import type { ListFilterValues } from "@/shared/components/common/ListFilterPopover";

export const initialTeacherListFilters: ListFilterValues = {
  board: "",
  class: "",
  section: "",
  /** Institute dropdown next to the search box; "" = all institutes. */
  institute_id: "",
  accountStatus: "",
};

export interface UploadState {
  isUploading: boolean;
  progress: number;
  fileName: string;
  fileSize: string;
  error: string | null;
}

export interface SuccessNotification {
  message: string;
  count?: number;
}

export interface TeachersListState {
  list: TeacherListRow[];
  uploadState: UploadState | null;
  successNotification: SuccessNotification | null;
  page: number;
  totalCount: number;
  listFilterValues: ListFilterValues;
  searchQuery: string;

  setPage: (page: number) => void;
  setTotalCount: (count: number) => void;
  setList: (list: TeacherListRow[]) => void;
  setListFilterValues: (values: ListFilterValues) => void;
  /** Also resets the page to 1. */
  setSearchQuery: (query: string) => void;
  setUploadState: (state: UploadState | null) => void;
  setSuccessNotification: (notification: SuccessNotification | null) => void;
  dismissSuccessNotification: () => void;
  removeTeacher: (id: string) => void;
  updateTeacher: (id: string, row: Omit<TeacherListRow, "id">) => void;
}

export const useTeachersListStore = create<TeachersListState>((set) => ({
  list: [],
  uploadState: null,
  successNotification: null,
  page: 1,
  totalCount: 0,
  listFilterValues: initialTeacherListFilters,
  searchQuery: "",

  setPage: (page) => set({ page }),
  setTotalCount: (totalCount) => set({ totalCount }),
  setList: (list) => set({ list }),
  setListFilterValues: (listFilterValues) => set({ listFilterValues }),
  setSearchQuery: (searchQuery) => set({ searchQuery, page: 1 }),
  setUploadState: (uploadState) => set({ uploadState }),
  setSuccessNotification: (successNotification) => set({ successNotification }),
  dismissSuccessNotification: () => set({ successNotification: null }),
  removeTeacher: (id) =>
    set((s) => ({ list: s.list.filter((r) => r.id !== id) })),
  updateTeacher: (id, row) =>
    set((s) => ({
      list: s.list.map((r) => (r.id === id ? { ...row, id } : r)),
    })),
}));
